<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <h1>Carriers</h1>
      <router-link class="btn btn-primary btn-sm" :to="{ name: 'CarrierNew' }"> <span class="mr-1 fas fa-plus"></span> New carrier </router-link>
    </div>
    <div class="card">
      <div class="card-body">
        <div class="py-3">
          <div class="input-group mb-2">
            <div class="input-group-prepend">
              <div class="input-group-text">
                <span class="fas fa-search"></span>
              </div>
            </div>
            <input class="form-control" :value="searchQuery" @input="startSearch" placeholder="Search" />
          </div>
          <div class="row">
            <div class="col-md-6 d-flex align-items-center" style="gap: 8px">
              <label for="from" class="mb-0 align-content-center">From:</label>
              <ValidatedSelect
                id="from"
                :value="startState"
                :options="stateOptions"
                :option-label-key="'title'"
                :option-value-key="'value'"
                :required="false"
                @input="newValue => updateRouteQuery({ startState: newValue })"
                class="mb-0"
              />
            </div>

            <div class="col-md-6 d-flex align-items-center" style="gap: 8px">
              <label for="to" class="mb-0 align-content-center">To:</label>
              <ValidatedSelect
                id="to"
                :value="endState"
                :options="stateOptions"
                :option-label-key="'title'"
                :option-value-key="'value'"
                :required="false"
                @input="newValue => updateRouteQuery({ endState: newValue })"
                class="mb-0"
              />
            </div>
          </div>
        </div>
        <div class="py-2">
          <PageLinks :current-page="currentPage" :has-next-page="hasNextPage" :on-page-change="updatePageUrlParam" :is-disabled="loading" />
        </div>

        <LoadingIndicator centered v-if="loading" />
        <table class="table" v-if="!loading && carriers && carriers.length > 0">
          <thead class="table">
            <tr>
              <TableSortableHeader class="px-1" label="Id" field="id" />
              <TableSortableHeader class="px-1" label="Company" field="company" />
              <th scope="col">Contact</th>
              <th scope="col">Phone</th>
              <th scope="col">Email</th>
              <th scope="col">Location</th>
              <TableSortableHeader class="px-1" label="Route" field="route_leads_count" />
              <TableSortableHeader class="px-1" label="Orders" field="leads_count" />
              <th scope="col">Last Order</th>
              <TableSortableHeader class="px-1" label="Trust Score" field="trust_score" style="width:120px">
                <div style="width: 20px;">
                <!-- Info Button with Tooltipom -->
                <b-button id="trust-score-info" variant="link" class="info-icon p-0" v-b-tooltip.hover>
                  <i class="fas fa-info-circle"></i>
                </b-button>
                <b-popover target="trust-score-info" triggers="hover focus" placement="top">
                  <div>
                    <div class="d-flex py-1" style="gap: 8px;">
                      <div class="d-flex justify-content-between" style="width:36px;">
                        <span class="rounded-circle d-inline-block circle bg-success"></span>
                        <span class="rounded-circle d-inline-block circle bg-success"></span>
                      </div>
                      <div>Status; Can Dispatch</div>
                    </div>
                    <!--div class="d-flex py-1" style="gap: 8px;">
                      <div style="width:36px;"><b>75</b></div>
                      <div>Trust Score Rating</div>
                    </div-->
                    <div class="d-flex py-1" style="gap: 8px;">
                      <div style="width:36px;"><i class="fas fa-flag red-flag"></i></div>
                      <div> Has Red Flags</div>
                    </div>
                  </div>
                </b-popover>
                </div>
              </TableSortableHeader>
            </tr>
          </thead>
          <tbody>
            <tr v-for="carrier in carriers" :key="carrier.id" :class="{'bg-bad': carrier.is_flagged}">
              <td>
                <router-link :to="{ name: 'CarrierDetails', params: { id: carrier.id } }">#{{ carrier.id }}</router-link>
              </td>
              <td>{{ carrier.contact.company }}</td>
              <td>
                <div v-for="contact in carrier.contact.contact.split(',')" :key="contact">{{contact}}</div>
              </td>
              <td style="width: 120px;">
                <div>
                  <a :href="`tel:${carrier.contact.phone}`">{{ carrier.contact.phone | formatPhone}}</a>
                </div>
                <div>
                  <a :href="`tel:${carrier.contact.phone2}`">{{ carrier.contact.phone2 | formatPhone }}</a>
                </div>
                <div>
                  <a :href="`tel:${carrier.contact.cellPhone}`">{{ carrier.contact.cellPhone | formatPhone }}</a>
                </div>
              </td>
              <td>
                <a :href="`mailto:${carrier.contact.email}`">{{ carrier.contact.email }}</a>
              </td>
              <td>
                <span v-if="carrier.location">{{ carrier.location.city }}, {{ carrier.location.state }} {{ carrier.location.zip }} </span>
              </td>
              <td>
                {{ carrier.routeLeadsCount }}
              </td>
              <td>
                {{ carrier.leadsCount }}
              </td>
              <td>
                <router-link v-if="carrier.lastOrder" :to="{ name: 'OrderDetails', params: { id: carrier.lastOrder.id } }">
                  {{ carrier.lastOrder.signedAt | formatDate}}
                </router-link>
              </td>
              <td>
                <div class="d-flex" style="gap:16px;">
                  <div>
                    <div class="rounded-circle d-inline-block circle" :class="carrier.isActive? 'bg-success':'bg-danger'"></div>
                    <div class="rounded-circle d-inline-block circle" :class="carrier.canDispatch? 'bg-success':'bg-danger'"></div>
                  </div>
                  <div class="d-flex" v-if="carrier.isFlagged">
                    <i class="fas fa-flag red-flag"></i>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <EmptyState v-if="!loading && !carriers" message="No carriers found matching search criteria" />

        <PageLinks :current-page="currentPage" :has-next-page="hasNextPage" :on-page-change="updatePageUrlParam" :is-disabled="loading" />
      </div>
    </div>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import statesMixin from '@/mixins/states';
import pageLinksMixin from '@/mixins/pageLinks';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import EmptyState from '@/components/shared/EmptyState';
import TableSortableHeader from '@/components/shared/TableSortableHeader';
import ValidatedSelect from '@/components/shared/ValidatedMultiselect.vue';
import { debounce } from 'debounce';

const updateSearchUrlParamDebounced = debounce(function (router, search) {
  router.push({ query: { search } });
}, 800);

export default {
  name: 'CarrierList',
  mixins: [pageLinksMixin, statesMixin],
  components: {
    LoadingIndicator,
    EmptyState,
    TableSortableHeader,
    ValidatedSelect
  },
  data() {
    return {
      carriers: [],
      loading: false,
      searchQuery: this.$route.query.search,
    };
  },
  computed: {
    startState() {
      const { startState } = this.$route.query;
      return startState;
    },
    endState() {
      const { endState } = this.$route.query;
      return endState;
    },
    hasNextPage() {
      return this.carriers.length === this.perPage;
    },
    fetchParams() {
      const params = {};
      const { startState, endState, sort, search } = this.$route.query;
      if (search) params.search = search;
      if (startState && endState) params.route = `${startState} to ${endState}`;
      if (sort) params.sort = sort;
      params.page = this.currentPage;
      params.perPage = this.perPage;
      return params;
    },
    stateOptions() {
      return this.states.map(x => ({
        title: `(${x.value}) ${x.label}`,
        value: x.value
      }));
    }
  },
  methods: {
    updateRouteQuery(newQuery) {
      const oldQuery = this.$route.query;
      const query = Object.assign({}, oldQuery, newQuery);
      this.$router.push({ query });
    },
    async fetch() {
      if (this.loading) return;
      this.loading = true;
      const response = await ApiClient.carriers.get({ params: this.fetchParams });
      this.loading = false;
      if (response.status == 200) {
        this.carriers = response.data.data;
      }
    },
    startSearch(event) {
      this.searchQuery = event.target.value;
      if (this.$route.query.search != this.searchQuery) {
        updateSearchUrlParamDebounced(this.$router, this.searchQuery);
      }
    },
  },
  watch: {
    fetchParams() {
      this.fetch();
    },
  },
  created() {
    this.fetch();
  },
};
</script>

<style lang="scss" scoped>
.circle {
  width: 16px;
  height: 16px;
  padding: 1px;
}

.info-icon {
  font-size: 18px;
  color: #6c757d;
  cursor: pointer;
  border: none;
}

/* Stilizacija Popovera */
.popover-content {
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* Crvena zastavica */
.red-flag {
  color: #d9534f;
  margin-right: 5px;
}
</style>
